import {
  Autocomplete,
  Box,
  Grid,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MdChevronLeft, MdChevronRight } from 'react-icons/md'
import { BatchNumberResult, CertificateExtractionResult } from 'src/api/certificate-extraction'
import { ExtractionConfidenceBadge } from '../components/ExtractionConfidenceBadge'
import { BatchNumberEditor } from '../components/BatchNumberEditor'
import { Spinner } from 'src/components/Spinner'
import { IProduct } from 'src/generated-types'
import { useGet } from 'src/api/reactQuery'
import { IPaginatedResult } from 'src/api/paginated-result'

interface ReviewStepProps {
  results: CertificateExtractionResult[]
  onResultsChange: (results: CertificateExtractionResult[]) => void
  fixedProductId?: EntityId
}

const getBatchStrings = (result: CertificateExtractionResult): string[] => {
  const batchValue = result.batch_numbers?.value
  if (!batchValue || !Array.isArray(batchValue)) return []
  return batchValue.map(b => (typeof b === 'string' ? b : (b as BatchNumberResult).raw))
}

const getBatchRanges = (result: CertificateExtractionResult): BatchNumberResult[] => {
  const batchValue = result.batch_numbers?.value
  if (!batchValue || !Array.isArray(batchValue)) return []
  return batchValue.filter(b => typeof b !== 'string' && (b as BatchNumberResult).is_range) as BatchNumberResult[]
}

// Sort products so supplier matches appear first
const sortProductsBySupplierMatch = (products: IProduct[], aiSupplier?: string | null): IProduct[] => {
  if (!aiSupplier || !products.length) return products
  const lowerSupplier = aiSupplier.toLowerCase()
  return [...products].sort((a, b) => {
    const aMatch = (a as any).supplier?.name?.toLowerCase().includes(lowerSupplier) ? 0 : 1
    const bMatch = (b as any).supplier?.name?.toLowerCase().includes(lowerSupplier) ? 0 : 1
    return aMatch - bMatch
  })
}

const PdfPreview: React.FC<{ fileUrl?: string; viewToken?: string; fileName?: string }> = ({ fileUrl, viewToken, fileName }) => {
  const { t } = useTranslation(['product-certification'])
  const [blobUrl, setBlobUrl] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(false)

  useEffect(() => {
    const previewUrl = viewToken
      ? `${(window as any).__RUNTIME_CONFIG__?.API_URL || process.env.REACT_APP_API_URL || ''}/api/files/download-file/${viewToken}?preview=true`
      : fileUrl

    if (!previewUrl) {
      setLoading(false)
      return
    }

    let cancelled = false
    const fetchPdf = async () => {
      try {
        const response = await fetch(previewUrl, { mode: 'cors', credentials: 'include' })
        if (!response.ok) throw new Error(`Failed to fetch: ${response.status}`)
        const blob = await response.blob()
        if (!cancelled) {
          setBlobUrl(URL.createObjectURL(blob) + '#toolbar=0&view=FitH')
          setLoading(false)
        }
      } catch (e) {
        console.error('PDF preview fetch failed:', e)
        if (!cancelled) {
          setError(true)
          setLoading(false)
        }
      }
    }

    fetchPdf()
    return () => {
      cancelled = true
      if (blobUrl) URL.revokeObjectURL(blobUrl)
    }
  }, [fileUrl, viewToken])

  if (loading) return <Stack justifyContent="center" alignItems="center" height="100%"><Spinner /></Stack>
  if (error || !blobUrl) {
    return (
      <Stack justifyContent="center" alignItems="center" height="100%">
        <Typography color="text.secondary">
          {t('previewNotAvailable', { ns: 'product-certification' })}
        </Typography>
      </Stack>
    )
  }

  return (
    <iframe
      src={blobUrl}
      style={{ width: '100%', height: '100%', border: 'none' }}
      title={fileName || 'Certificate preview'}
    />
  )
}

// Compact info chip for AI-extracted metadata (read-only display)
const InfoChip: React.FC<{ label: string; value?: string | null; confidence?: string }> = ({ label, value, confidence }) => {
  if (!value || confidence === 'not_found') return null
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', mr: 1, mb: 0.5 }}>
      <Typography variant="caption" color="text.secondary" mr={0.5}>{label}:</Typography>
      <Typography variant="caption" fontWeight={600}>{value}</Typography>
    </Box>
  )
}

export const ReviewStep: React.FC<ReviewStepProps> = ({ results, onResultsChange, fixedProductId }) => {
  const { t } = useTranslation(['product-certification', 'common', 'product'])
  const [currentIndex, setCurrentIndex] = useState(0)
  const [productSearch, setProductSearch] = useState('')
  const [debouncedSearch, setDebouncedSearch] = useState('')
  const autoSelectDone = useRef<Set<number>>(new Set())
  const [selectedProducts, setSelectedProducts] = useState<Record<number, IProduct>>({})

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(productSearch), 400)
    return () => clearTimeout(timer)
  }, [productSearch])

  const { data: productsData } = useGet<IPaginatedResult<IProduct>>(
    'product-certificates/products/search',
    { searchInput: debouncedSearch, page: 0, pageSize: 50 },
    { enabled: !fixedProductId && debouncedSearch.length >= 2 },
  )

  // Auto-set product search from AI-extracted product number when navigating
  useEffect(() => {
    if (!results?.length) return
    const pn = results[currentIndex]?.product_number?.value
    if (pn) setProductSearch(pn)
  }, [currentIndex])

  // Auto-select product if search returns exact match (only once per certificate)
  useEffect(() => {
    if (fixedProductId || !productsData?.items?.length || !results?.length) return
    if (autoSelectDone.current.has(currentIndex)) return

    const currentResult = results[currentIndex]
    if (!currentResult || currentResult.selectedProductId) {
      autoSelectDone.current.add(currentIndex)
      return
    }

    const pn = currentResult.product_number?.value
    if (!pn) return
    const aiSupplier = currentResult.supplier_name?.value?.toLowerCase()
    const numberMatches = productsData.items.filter((p: IProduct) => p.number === pn)

    if (numberMatches.length === 0) return

    const supplierMatch = aiSupplier
      ? numberMatches.find((p: any) => p.supplier?.name?.toLowerCase().includes(aiSupplier))
      : null

    const match = supplierMatch || numberMatches[0]
    autoSelectDone.current.add(currentIndex)
    setSelectedProducts(prev => ({ ...prev, [currentIndex]: match }))
    const updated = [...results]
    updated[currentIndex] = { ...updated[currentIndex], selectedProductId: match.id }
    onResultsChange(updated)
  }, [productsData])

  if (results.length === 0) {
    return (
      <Typography color="text.secondary" textAlign="center" py={4}>
        {t('noResults', { ns: 'product-certification' })}
      </Typography>
    )
  }

  const current = results[currentIndex]

  const updateBatchNumbers = (batches: string[]) => {
    const updated = [...results]
    const item = { ...updated[currentIndex] }
    item.batch_numbers = {
      ...item.batch_numbers,
      value: batches,
      confidence: batches.length > 0 ? 'high' : 'not_found',
    }
    updated[currentIndex] = item
    onResultsChange(updated)
  }

  const updateNotes = (notes: string) => {
    const updated = [...results]
    const item = { ...updated[currentIndex] }
    item.notes = { ...item.notes, value: notes, confidence: notes ? 'high' : 'not_found' }
    updated[currentIndex] = item
    onResultsChange(updated)
  }

  return (
    <Box>
      {/* Header with navigation */}
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">
          {t('certificateNofM', { ns: 'product-certification', current: currentIndex + 1, total: results.length })}
        </Typography>
        <Stack direction="row" spacing={1} alignItems="center">
          <IconButton onClick={() => setCurrentIndex(i => i - 1)} disabled={currentIndex === 0} size="small">
            <MdChevronLeft size={24} />
          </IconButton>
          <Typography variant="body2">{currentIndex + 1} / {results.length}</Typography>
          <IconButton onClick={() => setCurrentIndex(i => i + 1)} disabled={currentIndex === results.length - 1} size="small">
            <MdChevronRight size={24} />
          </IconButton>
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        {/* PDF Preview */}
        <Grid item xs={12} md={7}>
          <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, height: 'calc(100vh - 320px)', minHeight: 600, overflow: 'hidden', backgroundColor: '#f5f5f5' }}>
            <PdfPreview fileUrl={current.fileUrl} viewToken={current.viewToken} fileName={current.fileName} />
          </Box>
          <Typography variant="caption" color="text.secondary" mt={0.5} display="block">{current.fileName}</Typography>
        </Grid>

        {/* Right panel */}
        <Grid item xs={12} md={5}>
          <Stack spacing={2}>
            {/* AI-extracted info (read-only compact display) */}
            <Paper variant="outlined" sx={{ p: 1.5, backgroundColor: 'grey.50' }}>
              <Typography variant="caption" fontWeight={700} color="text.secondary" mb={0.5} display="block">
                {t('detectedPattern', { ns: 'product-certification' })}: {current.detected_pattern_name || '-'}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap' }}>
                <InfoChip label={t('supplier', { ns: 'common' })} value={current.supplier_name?.value} confidence={current.supplier_name?.confidence} />
                <InfoChip label={t('productNumber', { ns: 'product-certification' })} value={current.product_number?.value} confidence={current.product_number?.confidence} />
                <InfoChip label={t('productName', { ns: 'product-certification' })} value={current.product_name?.value} confidence={current.product_name?.confidence} />
                <InfoChip label={t('certificateNumber', { ns: 'product-certification' })} value={current.certificate_number?.value} confidence={current.certificate_number?.confidence} />
                <InfoChip label={t('mbl', { ns: 'product-certification' })} value={current.mbl?.value} confidence={current.mbl?.confidence} />
                <InfoChip label={t('certificateDate', { ns: 'product-certification' })} value={current.certificate_date?.value} confidence={current.certificate_date?.confidence} />
              </Box>
            </Paper>

            {/* Product Selector (only when no fixed product) */}
            {!fixedProductId && (
              <Box>
                <Typography variant="subtitle2" mb={0.5}>
                  {t('selectProduct', { ns: 'product', defaultValue: 'Produkt' })} *
                </Typography>
                <Autocomplete
                  options={sortProductsBySupplierMatch(productsData?.items || [], current.supplier_name?.value)}
                  getOptionLabel={(option: any) =>
                    typeof option === 'string' ? option : `${option.number} - ${option.description} (${option.supplier?.name || ''})`
                  }
                  value={selectedProducts[currentIndex] || null}
                  onInputChange={(_, value, reason) => {
                    if (reason === 'input') setProductSearch(value)
                  }}
                  onChange={(_, value) => {
                    const product = value as IProduct | null
                    setSelectedProducts(prev => {
                      const next = { ...prev }
                      if (product) next[currentIndex] = product
                      else delete next[currentIndex]
                      return next
                    })
                    const updated = [...results]
                    updated[currentIndex] = { ...updated[currentIndex], selectedProductId: product?.id }
                    onResultsChange(updated)
                  }}
                  renderOption={(props, option: any) => (
                    <Box component="li" {...props} key={option.id}>
                      <Box>
                        <Typography variant="body2" fontWeight={600}>
                          {option.number} - {option.description}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.supplier?.name || ''}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  renderInput={params => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={t('searchProduct', { ns: 'product', defaultValue: 'Søk etter produktnummer eller navn...' })}
                      error={!current.selectedProductId}
                    />
                  )}
                  isOptionEqualToValue={(option: any, value: any) => option.id === value?.id}
                  filterOptions={x => x}
                />
              </Box>
            )}

            {/* Batch Numbers (editable) */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="subtitle2">
                  {t('batchNumbers', { ns: 'product-certification' })}
                </Typography>
                <ExtractionConfidenceBadge confidence={current.batch_numbers?.confidence || 'not_found'} />
              </Stack>
              <BatchNumberEditor
                value={getBatchStrings(current)}
                onChange={updateBatchNumbers}
                ranges={getBatchRanges(current)}
              />
            </Box>

            {/* Notes (editable) */}
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={0.5}>
                <Typography variant="subtitle2">
                  {t('notes', { ns: 'product-certification' })}
                </Typography>
                <ExtractionConfidenceBadge confidence={current.notes?.confidence || 'not_found'} />
              </Stack>
              <TextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={current.notes?.value || ''}
                onChange={e => updateNotes(e.target.value)}
                placeholder={t('notDetected', { ns: 'product-certification' })}
              />
            </Box>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  )
}
